// ============================================================================
// Branding Context
// Manages white-label branding state with localStorage persistence
// ============================================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrandingConfig, brandingService } from '../services/branding.service';

// ============================================================================
// Types
// ============================================================================

interface BrandingContextType {
  branding: BrandingConfig | null;
  isLoading: boolean;
  error: string | null;
  isBrandedLogin: boolean; // True if user logged in via branded login page
  loadBrandingBySlug: (slug: string) => Promise<boolean>;
  loadMyBranding: () => Promise<void>;
  clearBranding: () => void;
  applyBrandingColors: (config: BrandingConfig | null) => void;
  setBranding: (branding: BrandingConfig | null) => void;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const STORAGE_KEY = 'gumahub_branding';
const BRANDED_LOGIN_KEY = 'gumahub_branded_login'; // Flag to indicate user logged in via branded page

// ============================================================================
// Provider
// ============================================================================

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Don't initialize from localStorage - always start fresh and load from API
  // This prevents showing stale branding from a different company
  const [branding, setBrandingState] = useState<BrandingConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if this session was initiated via a branded login page
  const [isBrandedLogin, setIsBrandedLogin] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(BRANDED_LOGIN_KEY) === 'true';
    }
    return false;
  });

  // Apply CSS variables based on branding config
  const applyBrandingColors = useCallback((config: BrandingConfig | null) => {
    const root = document.documentElement;

    if (config) {
      // Apply brand colors as CSS variables
      if (config.primaryColor) {
        root.style.setProperty('--brand-primary', config.primaryColor);
      }
      if (config.secondaryColor) {
        root.style.setProperty('--brand-secondary', config.secondaryColor);
      }
      if (config.accentColor) {
        root.style.setProperty('--brand-accent', config.accentColor);
      }
    } else {
      // Reset to defaults
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-secondary');
      root.style.removeProperty('--brand-accent');
    }
  }, []);

  // Apply branding colors when branding changes
  useEffect(() => {
    applyBrandingColors(branding);
  }, [branding, applyBrandingColors]);

  // Load branding by slug (for branded login page)
  // This marks the session as a "branded login" so branding persists after login
  const loadBrandingBySlug = useCallback(async (slug: string): Promise<boolean> => {
    // Clear cached branding first to prevent showing stale data from another company
    setBrandingState(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(BRANDED_LOGIN_KEY);
    setIsBrandedLogin(false);
    applyBrandingColors(null);

    setIsLoading(true);
    setError(null);

    try {
      const response = await brandingService.getBrandingBySlug(slug);
      if (response.success && response.data) {
        setBrandingState(response.data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data));
        // Mark this as a branded login session - branding will persist after login
        localStorage.setItem(BRANDED_LOGIN_KEY, 'true');
        setIsBrandedLogin(true);
        return true;
      } else {
        setError(response.error || 'Branding not found');
        return false;
      }
    } catch (err) {
      console.error('Error loading branding:', err);
      setError('Failed to load branding');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [applyBrandingColors]);

  // Load current user's client branding
  // Skips loading if user logged in via a branded login page (preserves that branding)
  const loadMyBranding = useCallback(async () => {
    // If this was a branded login, preserve that branding instead of loading user's company branding
    const wasBrandedLogin = localStorage.getItem(BRANDED_LOGIN_KEY) === 'true';
    if (wasBrandedLogin) {
      // Load branding from localStorage (set during branded login)
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const storedBranding = JSON.parse(stored);
          setBrandingState(storedBranding);
          applyBrandingColors(storedBranding);
          setIsBrandedLogin(true);
          return;
        }
      } catch {
        // Fall through to load from API if localStorage parse fails
      }
    }

    // Clear cached branding first to prevent showing stale data
    setBrandingState(null);
    localStorage.removeItem(STORAGE_KEY);
    applyBrandingColors(null);

    setIsLoading(true);
    setError(null);

    try {
      const response = await brandingService.getMyBranding();
      if (response.success) {
        if (response.data) {
          setBrandingState(response.data);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(response.data));
        } else {
          // No branding configured for this client
          setBrandingState(null);
          localStorage.removeItem(STORAGE_KEY);
        }
      } else {
        setError(response.error || 'Failed to load branding');
      }
    } catch (err) {
      console.error('Error loading my branding:', err);
      setError('Failed to load branding');
    } finally {
      setIsLoading(false);
    }
  }, [applyBrandingColors]);

  // Clear branding (for standard login or logout)
  const clearBranding = useCallback(() => {
    setBrandingState(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(BRANDED_LOGIN_KEY);
    setIsBrandedLogin(false);
    applyBrandingColors(null);
    setError(null);
  }, [applyBrandingColors]);

  // Set branding directly (e.g., after login)
  const setBranding = useCallback((newBranding: BrandingConfig | null) => {
    setBrandingState(newBranding);
    if (newBranding) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newBranding));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  return (
    <BrandingContext.Provider
      value={{
        branding,
        isLoading,
        error,
        isBrandedLogin,
        loadBrandingBySlug,
        loadMyBranding,
        clearBranding,
        applyBrandingColors,
        setBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;
